import { datadogRum } from '@datadog/browser-rum'
import { useDialog } from '@dni-hooks/useDialog'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { useTimeout } from '@dni-react-hooks/use-timeout'
import { isMobileDevice } from '@dni-utils/is-mobile-device'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowRightLarge } from '@pets-ui/Icons'
import { Image } from '@pets-ui/Image/Image'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { Suspense, useEffect, useState } from 'react'
import InvalidQRCodeImage from '../assets/invalid-qr-code.png'
import { LazyQRCodeCanvas } from './LazyQRCodeCanvas'
interface DialogQRCodeProps {
  url: string
  qrCodeSize?: number
  onRefresh?: () => Promise<string | undefined>
}

export const DIALOG_PAY_ME_PAY_QR_CODE = 'DIALOG_PAY_ME_PAY_QR_CODE'

const COUNTDOWN_DURATION_SECONDS = 10 * 60

export const DialogPayMeQRCode = ({ url, qrCodeSize: size = 224, onRefresh }: DialogQRCodeProps) => {
  const { open } = useDialog(DIALOG_PAY_ME_PAY_QR_CODE)
  const _isMobileDevice = isMobileDevice()
  useEffectOnce(() => {
    if (!_isMobileDevice) return
    window.location.href = url
  })

  /**
   * payment execution後取得的redirect_url若是qrcode，則由前端顯示qrcode
   */
  const { openDialog: openDialogQRCode } = useDialog(DIALOG_PAY_ME_PAY_QR_CODE)
  useEffect(() => {
    if (!url) {
      return
    }
    openDialogQRCode()
  }, [openDialogQRCode, url])

  const [isExpired, setIsExpired] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()

  useTimeout(() => setIsExpired(true), isExpired ? null : COUNTDOWN_DURATION_SECONDS * 1000)

  const handleRefreshClick = async () => {
    if (!onRefresh) return
    try {
      setIsRefreshing(true)
      const newUrl = await onRefresh()
      if (newUrl) {
        setIsExpired(false)
      }
      else {
        openSnackbar({ message: '取得新付款條碼失敗，請稍後再試', severity: 'error' })
      }
    }
    catch (error) {
      datadogRum.addError(error, { function: '[DialogPayMeQRCode] refresh failed' })
      console.error('[DialogPayMeQRCode] refresh failed:', error)
      openSnackbar({ message: '取得新付款條碼失敗，請稍後再試', severity: 'error' })
    }
    finally {
      setIsRefreshing(false)
    }
  }

  return (
    <Dialog open={open} maxWidth={480}>
      <DialogHeader align="center" title={isExpired ? 'PayCode 已失效！請重試' : '請用 PayMe 掃描此 PayCode'} />
      <DialogContent>
        <VStack gap="12px">
          <HStack justifyContent="center">
            <Text>在交易完成前請不要關閉此頁面</Text>
          </HStack>
          <HStack justifyContent="center" pl={10} pr={10}>
            {isExpired
              ? (
                <Image src={InvalidQRCodeImage} alt="PayCode 已失效" width={size} height={size} />
              )
              : (
                <Suspense
                  fallback={(
                    <Text variant="body" color={colors.grey600}>
                      加載中
                    </Text>
                  )}
                >
                  <LazyQRCodeCanvas value={url} size={size} />
                </Suspense>
              )}
          </HStack>
          <Box justifyContent="center" backgroundColor={colors.grey50} p="12px" borderRadius="8px">
            <VStack gap="10px">
              <HStack justifyContent="center">
                <Text variant="subtitle" fontWeight={600}>如何使用PayMe付款？</Text>
              </HStack>
              <HStack gap="8px" justifyContent="center">
                <Text>1. 打開 PayMe 應用程式</Text>
                <HStack alignItems="center">
                  <ArrowRightLarge />
                </HStack>
                <Text>2. 掃描 PayCode 以授權付款</Text>
                <HStack alignItems="center">
                  <ArrowRightLarge />
                </HStack>
                <Text>3. 在 PayMe 完成交易後，請在此頁面等待確認</Text>
              </HStack>
            </VStack>
          </Box>
          {isExpired && onRefresh && (
            <HStack justifyContent="center">
              <Button
                variant="filled"
                color="red"
                onClick={handleRefreshClick}
                loading={isRefreshing}
                disabled={isRefreshing}
              >
                取得新付款條碼
              </Button>
            </HStack>
          )}
        </VStack>
      </DialogContent>
    </Dialog>
  )
}
